import React from 'react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip 
} from 'recharts';
import { motion } from 'framer-motion';
import { colorScale } from '../Utilities/helpers';

const ErrorsChart = ({ data, themeColors }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6 h-[420px] flex flex-col">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-5">
        Error Distribution
      </h2>
      <div className="h-[200px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              innerRadius={40}
              dataKey="count"
              nameKey="code"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              stroke="transparent"
              className="[&_.recharts-sector]:stroke-white dark:[&_.recharts-sector]:stroke-gray-800"
            >
              {data?.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={index === 0 ? themeColors.warning : colorScale(index / 5)}
                  className="transition-transform duration-300 hover:scale-105 origin-center"
                  strokeWidth={2}
                  stroke={themeColors.primary}
                  style={{
                    transition: 'transform 0.3s, filter 0.3s',
                    filter: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.filter = 'brightness(1.1)';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.filter = 'none';
                    e.target.style.transform = 'scale(1)';
                  }}
                />
              ))}
            </Pie>
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="backdrop-blur-md bg-white/90 dark:bg-gray-700/95 p-3 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 transform transition-all">
                      <div className="w-full h-1 rounded-full mb-2" style={{ 
                        backgroundColor: payload[0].color,
                        boxShadow: `0 0 8px ${payload[0].color}33`
                      }}></div>
                      <div className="space-y-2">
                        <p className="font-bold text-gray-800 dark:text-gray-100">
                          Error {data.code}
                          <span className="font-normal text-gray-600 dark:text-gray-300">
                            {' '}({data.description})
                          </span>
                        </p>
                        <div className="pt-2 border-t border-gray-200/50 dark:border-gray-600">
                          <p className="text-lg font-bold text-gray-800 dark:text-gray-100">
                            {data.count}
                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                              {' '}occurrences
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }} 
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Enhanced legend with hover states */}
      <div className="mt-4 grid grid-cols-2 gap-3 flex-grow overflow-auto">
        {data?.map((error, index) => (
          <motion.div 
            key={`legend-${index}`} 
            className="flex items-center p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all cursor-pointer"
            whileHover={{ scale: 1.00 }}
            transition={{ duration: 0.2 }}
          >
            <div 
              className="w-3 h-3 rounded-full mr-2 flex-shrink-0 shadow-sm" 
              style={{ 
                backgroundColor: index === 0 ? themeColors.warning : colorScale(index / 5),
                boxShadow: `0 0 8px ${(index === 0 ? themeColors.warning : colorScale(index / 5))}40`
              }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
              {error.description}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ErrorsChart; 